import { Link } from '@inertiajs/react'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import TrustpilotWidget from '@/components/marketing/TrustpilotWidget'
import { track } from '@/lib/analytics'
import { postMetaEventPing } from '@/lib/metaEventPing'
import type { SharedMarketingProps } from '@/types/marketing'

type Currency = 'USD' | 'EUR' | 'INR' | 'THB'
type BillingCycle = 'monthly' | 'yearly'

interface PricingDatum {
  currency: Currency
  actual_price: number
  discounted_price: number
}

interface PricingPageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
    bg_image?: string
    highlights: string[]
  }
  pricing: Record<string, Record<BillingCycle, Record<Currency, PricingDatum>>>
  default_currency: Currency
  supported_currencies: Currency[]
  plans: Record<string, {
    name: string
    subtitle: string
    badge?: string | null
    cta: { label: string; href: string }
    bullets: string[]
  }>
  faq_items: { question: string; answer: string }[]
  feature_categories: {
    name: string
    features: {
      name: string
      description: string
      [key: string]: boolean | string
    }[]
  }[]
  final_cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
}

// Label shown in the currency toggle. Just the 3-letter code so the
// selector renders USD / EUR / INR / THB instead of the previous
// USD-default fallback that caused THB to display as USD.
function getCurrencySymbol(currency: Currency) {
  return currency
}

function formatPrice(price: number, currency: Currency) {
  const locale =
    currency === 'INR' ? 'en-IN' :
    currency === 'THB' ? 'th-TH' :
    'en-US'
  const fractionDigits = currency === 'INR' || currency === 'THB' ? 0 : 2
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: fractionDigits
  }).format(price)
}

export default function Pricing(props: PricingPageProps) {
  const [currency, setCurrency] = useState<Currency>(props.default_currency)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('yearly')

  useEffect(() => {
    const storedBilling = window.localStorage.getItem('wavbee-billing-cycle') as BillingCycle | null
    if (storedBilling === 'monthly' || storedBilling === 'yearly') setBillingCycle(storedBilling)
  }, [])

  useEffect(() => {
    window.localStorage.setItem('wavbee-billing-cycle', billingCycle)
  }, [billingCycle])

  // view_pricing — fire once on mount with the plan keys visible on the page.
  useEffect(() => {
    const eventId = track('view_pricing', {
      metaEvent: 'ViewContent',
      data: { plans_visible: Object.keys(props.plans) },
    })
    postMetaEventPing('ViewContent', eventId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleSelectPlan(planSlug: string) {
    track('select_plan', { data: { plan_slug: planSlug, cta_position: 'plan_card' } })
  }

  return (
    <>
      <section className="marketing-hero-section" style={props.hero.bg_image ? {
        backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.55), rgba(5,5,5,0.85)), url(${props.hero.bg_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : undefined}>
        <div className="container mx-auto px-6 pb-14 pt-10 lg:pb-20 lg:pt-14">
          <div className="mx-auto max-w-4xl text-center">
            <div className="marketing-eyebrow" style={{ justifyContent: 'center' }}>{props.hero.eyebrow}</div>
            <h1 className="marketing-display mt-5">{props.hero.title}</h1>
            <p className="marketing-lead mx-auto mt-5 max-w-3xl">{props.hero.body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {props.hero.highlights.map((highlight) => (
                <span key={highlight} className="marketing-chip">{highlight}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-surface-card">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-semibold uppercase tracking-[0.2em] text-white/55">
                    Currency
                  </span>
                  <div className="marketing-toggle">
                    {props.supported_currencies.map((item) => (
                      <button
                        key={item}
                        type="button"
                        aria-pressed={currency === item}
                        className={`marketing-toggle-button ${currency === item ? 'is-active' : ''}`}
                        onClick={() => setCurrency(item)}
                      >
                        {getCurrencySymbol(item)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="marketing-toggle">
                  {(['monthly', 'yearly'] as BillingCycle[]).map((item) => (
                    <button
                      key={item}
                      type="button"
                      aria-pressed={billingCycle === item}
                      className={`marketing-toggle-button ${billingCycle === item ? 'is-active' : ''}`}
                      onClick={() => setBillingCycle(item)}
                    >
                      {item === 'yearly' ? 'Yearly' : 'Monthly'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Artist plans */}
          <h3 className="mt-10 mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-white/55">For Artists</h3>
          <div className="grid gap-6 md:grid-cols-2">
            {Object.keys(props.plans).filter(k => k.startsWith('artist')).map((planKey, index) => {
              const plan = props.plans[planKey]
              const price = props.pricing[planKey]?.[billingCycle]?.[currency]
              if (!price) return null

              return (
                <ScrollReveal key={planKey} direction="up" delay={index * 0.12}>
                  <div className={`marketing-pricing-card h-full ${plan.badge ? 'marketing-pricing-card-popular' : ''}`}>
                    {plan.badge && <div className="marketing-pricing-badge">{plan.badge}</div>}
                    <div>
                      <h2 className="text-3xl font-semibold text-white">{plan.name}</h2>
                      <p className="mt-2 text-sm text-white/64">{plan.subtitle}</p>
                    </div>

                    <div className="mt-8">
                      <div className="text-5xl font-bold tracking-tight text-white">{formatPrice(price.discounted_price / (billingCycle === 'yearly' ? 12 : 1), currency)}</div>
                      <div className="mt-2 text-sm text-white/58">
                        {billingCycle === 'yearly' ? `${formatPrice(price.discounted_price, currency)} billed yearly` : 'Billed monthly'}
                      </div>
                      <div className="mt-2 text-sm text-white/34 line-through">
                        {formatPrice(price.actual_price / (billingCycle === 'yearly' ? 12 : 1), currency)} / month
                      </div>
                    </div>

                    <ul className="mt-8 space-y-3 text-sm text-white/82">
                      {plan.bullets.map((bullet) => (
                        <li key={bullet} className="marketing-bullet-item">{bullet}</li>
                      ))}
                    </ul>

                    <div className="mt-8">
                      <a href={plan.cta.href} className="marketing-button-primary" onClick={() => handleSelectPlan(planKey)}>
                        {plan.cta.label}
                      </a>
                    </div>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>

          {/* Label plans */}
          <h3 className="mt-10 mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-white/55">For Labels</h3>
          <div className="grid gap-6 md:grid-cols-3">
            {Object.keys(props.plans).filter(k => k.startsWith('label')).map((planKey, index) => {
              const plan = props.plans[planKey]
              const price = props.pricing[planKey]?.[billingCycle]?.[currency]
              if (!price) return null

              return (
                <ScrollReveal key={planKey} direction="up" delay={index * 0.12}>
                  <div className={`marketing-pricing-card h-full ${plan.badge ? 'marketing-pricing-card-popular' : ''}`}>
                    {plan.badge && <div className="marketing-pricing-badge">{plan.badge}</div>}
                    <div>
                      <h2 className="text-3xl font-semibold text-white">{plan.name}</h2>
                      <p className="mt-2 text-sm text-white/64">{plan.subtitle}</p>
                    </div>

                    <div className="mt-8">
                      <div className="text-5xl font-bold tracking-tight text-white">{formatPrice(price.discounted_price / (billingCycle === 'yearly' ? 12 : 1), currency)}</div>
                      <div className="mt-2 text-sm text-white/58">
                        {billingCycle === 'yearly' ? `${formatPrice(price.discounted_price, currency)} billed yearly` : 'Billed monthly'}
                      </div>
                      <div className="mt-2 text-sm text-white/34 line-through">
                        {formatPrice(price.actual_price / (billingCycle === 'yearly' ? 12 : 1), currency)} / month
                      </div>
                    </div>

                    <ul className="mt-8 space-y-3 text-sm text-white/82">
                      {plan.bullets.map((bullet) => (
                        <li key={bullet} className="marketing-bullet-item">{bullet}</li>
                      ))}
                    </ul>

                    <div className="mt-8">
                      <a href={plan.cta.href} className="marketing-button-primary" onClick={() => handleSelectPlan(planKey)}>
                        {plan.cta.label}
                      </a>
                    </div>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow marketing-eyebrow-dark">Compare features</div>
              <h2 className="marketing-section-title marketing-section-title-dark">See what's included in each plan.</h2>
            </div>
          </ScrollReveal>

          <div className="space-y-10">
            {props.feature_categories.map((category, catIndex) => (
              <ScrollReveal key={category.name} direction="up" delay={catIndex * 0.08}>
                <div className="marketing-feature-table-wrap">
                  <h3 className="text-2xl font-semibold text-[#151515]">{category.name}</h3>
                  <div className="mt-5 overflow-hidden rounded-[28px] border border-black/8 bg-white shadow-[0_30px_70px_rgba(10,10,10,0.08)]">
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-[#121212] text-white">
                          <tr>
                            <th className="px-5 py-4 text-left text-sm font-semibold">Feature</th>
                            {Object.keys(props.plans).map((planKey) => (
                              <th key={planKey} className="px-3 py-4 text-center text-sm font-semibold whitespace-nowrap">
                                {props.plans[planKey].name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {category.features.map((feature) => (
                            <tr key={feature.name as string} className="border-t border-black/6">
                              <td className="px-5 py-4 align-top">
                                <div className="font-semibold text-[#141414]">{feature.name as string}</div>
                                <div className="mt-1 text-sm leading-6 text-[#505050]">{feature.description as string}</div>
                              </td>
                              {Object.keys(props.plans).map((planKey) => (
                                <td key={planKey} className="px-3 py-4 text-center align-middle">
                                  {feature[planKey] ? (
                                    <Check className="mx-auto h-5 w-5 text-green-600" aria-label="Included" />
                                  ) : (
                                    <span className="text-lg text-[#b0b0b0]" aria-label="Not included">—</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trustpilot */}
      <section className="container mx-auto px-6 py-8">
        <div className="mx-auto max-w-md text-center">
          <TrustpilotWidget />
        </div>
      </section>

      <section id="faq" className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">FAQ</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Pricing questions</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <FaqAccordion items={props.faq_items} />
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.15}>
            <div className="mt-8 text-center">
              <Link href="/help" className="text-sm font-medium text-[#0f0f0f]/60 hover:text-[#ff6a1a] transition-colors" prefetch>
                See all FAQs →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow">Get started</div>
                <h2 className="marketing-section-title">{props.final_cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.final_cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.final_cta.actions.map((action, index) => {
                  const className = index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'
                  return action.href.includes('/apply') ? (
                    <a key={action.href} href={action.href} className={className}>
                      {action.label}
                    </a>
                  ) : (
                    <Link key={action.href} href={action.href} className={className} prefetch>
                      {action.label}
                    </Link>
                  )
                })}
              </div>
            </div>
          </ScrollReveal>
          {props.layout?.comparisons_enabled && (
            <ScrollReveal direction="up" delay={0.1}>
              <div className="mt-8 flex flex-wrap justify-center gap-3 text-sm">
                <span className="text-white/40">See how we compare:</span>
                <Link href="/compare/distrokid" className="text-white/64 hover:text-[#ff6a1a] transition-colors" prefetch>vs DistroKid</Link>
                <Link href="/compare/tunecore" className="text-white/64 hover:text-[#ff6a1a] transition-colors" prefetch>vs TuneCore</Link>
                <Link href="/compare/cdbaby" className="text-white/64 hover:text-[#ff6a1a] transition-colors" prefetch>vs CD Baby</Link>
              </div>
            </ScrollReveal>
          )}
        </div>
      </section>
    </>
  )
}
